import React, { useState, useEffect } from 'react';
import { Button } from '../../components/ui/button';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Textarea } from '../../components/ui/textarea';
import { toast } from 'sonner';
import { Loader2, FileType, Save } from 'lucide-react';
import api from '../../lib/api';

export function PdfFooterSettings() {
  const [pdfFooterText, setPdfFooterText] = useState('');
  const [systemFooterText, setSystemFooterText] = useState('');
  const [savingFooter, setSavingFooter] = useState(false);
  const [loadingSettings, setLoadingSettings] = useState(false);

  useEffect(() => {
    loadPdfSettings();
  }, []);

  const loadPdfSettings = async () => {
    setLoadingSettings(true);
    try {
      const response = await api.get('/settings');
      const dispatchTemplate = response.data?.dispatch_note_template || {};
      setPdfFooterText(dispatchTemplate.footerText || '');
      setSystemFooterText(dispatchTemplate.systemFooterText || 'System Generated Document - Dispatch Note - Voro Production Suite for Food Manufacturing');
    } catch {
      // Silently handle
    } finally {
      setLoadingSettings(false);
    }
  };

  const handleSaveFooterText = async () => {
    setSavingFooter(true);
    try {
      const currentSettingsRes = await api.get('/settings');
      const currentSettings = currentSettingsRes.data || {};
      const currentTemplate = currentSettings.dispatch_note_template || {};
      await api.put('/settings', {
        ...currentSettings,
        dispatch_note_template: {
          ...currentTemplate,
          footerText: pdfFooterText,
          systemFooterText: systemFooterText
        }
      });
      toast.success('PDF footer settings saved successfully');
    } catch (error) {
      toast.error('Failed to save: ' + (error.response?.data?.detail || error.message));
    } finally {
      setSavingFooter(false);
    }
  };

  return (
    <Card className="border-purple-200 bg-purple-50/30" data-testid="pdf-footer-settings">
      <CardHeader>
        <CardTitle className="text-purple-700 flex items-center gap-2">
          <FileType className="w-5 h-5" />
          Dispatch Note PDF Settings
        </CardTitle>
        <CardDescription>Customize the footer text that appears on dispatch note PDFs</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div>
          <Label htmlFor="pdf-footer-text" className="text-sm font-medium">Footer Text (Company Info)</Label>
          <p className="text-xs text-muted-foreground mb-2">
            Company address and contact details. Recommended: Cashel Farmhouse Cheesemakers, Beechmount, Fethard, Co. Tipperary, E91 E8W8. Telephone: +353 5261 31151 info@cashelblue.com
          </p>
          {loadingSettings ? (
            <div className="flex items-center gap-2 py-2">
              <Loader2 className="w-4 h-4 animate-spin" />
              <span className="text-sm text-muted-foreground">Loading settings...</span>
            </div>
          ) : (
            <Textarea
              id="pdf-footer-text"
              value={pdfFooterText}
              onChange={(e) => setPdfFooterText(e.target.value)}
              placeholder="Cashel Farmhouse Cheesemakers, Beechmount, Fethard, Co. Tipperary, E91 E8W8. Telephone: +353 5261 31151 info@cashelblue.com"
              rows={2}
              className="mt-1"
              data-testid="pdf-footer-text-input"
            />
          )}
        </div>
        <div>
          <Label htmlFor="system-footer-text" className="text-sm font-medium text-gray-500">System Footer Text (Developer Only)</Label>
          <p className="text-xs text-muted-foreground mb-2">
            Permanent grey text at the very bottom. Format: "System Generated Document - Document type - Reference number - System name"
          </p>
          <Textarea
            id="system-footer-text"
            value={systemFooterText}
            onChange={(e) => setSystemFooterText(e.target.value)}
            placeholder="System Generated Document - Dispatch Note - Voro Production Suite for Food Manufacturing"
            rows={2}
            className="mt-1 text-gray-500"
            data-testid="system-footer-text-input"
          />
        </div>
        <div className="flex items-center gap-2">
          <Button
            onClick={handleSaveFooterText}
            disabled={savingFooter || loadingSettings}
            className="bg-purple-600 hover:bg-purple-700"
            data-testid="btn-save-pdf-footer"
          >
            {savingFooter ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving...</>
            ) : (
              <><Save className="w-4 h-4 mr-2" /> Save Footer Settings</>
            )}
          </Button>
          <span className="text-xs text-muted-foreground">Changes will apply to all future dispatch note PDFs</span>
        </div>
      </CardContent>
    </Card>
  );
}
